import { useState, useRef, useEffect } from "react";
import accountIcon from "../../assets/accountIcon.svg";
import { useAuth0 } from "@auth0/auth0-react";
import { Box, Button, Divider, IconButton, Menu } from "@mui/material";
import { PureLightThemeCaaS } from "src/theme/schemes/PureLightTheme";
import { Browser } from "@capacitor/browser";
import { mobile, isMobileWebBrowser } from "src/utils/platformUtils";
import { trackSignupScreenSeenAt } from "src/utils/analyticsCapFirebase";


const DROPDOWN_ORANGE = `${PureLightThemeCaaS.colors.alpha.orange[100]}`;

export default function AccountSpeedDial() {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const paperRef = useRef<HTMLDivElement>(null);
    const open = Boolean(anchorEl);
    const { loginWithRedirect } = useAuth0();

    const isMobile = isMobileWebBrowser() || mobile;

    const handleIconClick = (event: React.MouseEvent<HTMLElement>) => {
        if (open) {
            setAnchorEl(null);
        } else {
            setAnchorEl(event.currentTarget);
        }
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    // Close on click outside (MUI backdrop/outside often not firing in this app)
    useEffect(() => {
        if (!open || !anchorEl) return;
        const handleClickOutside = (ev: MouseEvent | TouchEvent) => {
            const target = ev.target as Node;
            if (anchorEl === target || anchorEl.contains(target)) return;
            if (paperRef.current?.contains(target)) return;
            setAnchorEl(null);
        };
        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("touchstart", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("touchstart", handleClickOutside);
        };
    }, [open, anchorEl]);

    const handleLogin = async (event: React.MouseEvent) => {
        event.stopPropagation();
        handleMenuClose();
        await trackSignupScreenSeenAt("manual_click", "header");
        await loginWithRedirect({
            async openUrl(url) {
                await Browser.open({
                    url,
                    windowName: "_self",
                });
            },
        });
    };

    return (
        <>
            {/* Pill-style auth buttons (preview — remove if keeping icon menu only) */}
            <Box
                border={"1px solid"}
                borderColor={`${PureLightThemeCaaS.colors.alpha.white[30]}`}
                sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "999px",
                    backgroundColor: "rgba(0, 0, 0, 0.1)",
                    backdropFilter: "blur(8px)",
                    WebkitBackdropFilter: "blur(8px)",
                    // border: "1px solid rgba(255, 255, 255, 0.12)",
                    borderWidth: "1.2px",
                    px: 1,
                    py: 1,
                    height: "31px",
                    maxWidth: isMobile ? "140px" : "160px",
                    zIndex: 1500,
                    flexShrink: 0,
                }}
            >
                <Button
                    variant="text"
                    size="small"
                    onClick={handleLogin}
                    disableElevation
                    disableRipple
                    sx={{
                        minWidth: 0,
                        p: 0,
                        fontSize: isMobile ? "11px" : "14px",
                        fontWeight: 500,
                        lineHeight: 1.2,
                        color: "#fff",
                        // textTransform: "uppercase",
                        // letterSpacing: "0.03em",
                        "&:hover": { backgroundColor: "transparent", color: "#fff" },
                    }}
                >
                    Login
                </Button>
                <Box
                    component="span"
                    sx={{
                        width: "1px",
                        height: "15px",
                        mx: 0.5,
                        backgroundColor: "rgba(255, 255, 255, 0.3)",
                        flexShrink: 0,
                    }}
                />
                <Button
                    variant="text"
                    size="small"
                    onClick={handleLogin}
                    disableElevation
                    disableRipple
                    sx={{
                        minWidth: 0,
                        p: 0,
                        fontSize: isMobile ? "11px" : "14px",
                        fontWeight: 500,
                        lineHeight: 1.2,
                        color: DROPDOWN_ORANGE,
                        // textTransform: "uppercase",
                        // letterSpacing: "0.03em",
                        whiteSpace: "nowrap",
                        "&:hover": { backgroundColor: "transparent", color: DROPDOWN_ORANGE },
                    }}
                >
                    Sign Up Free
                </Button>
            </Box>

            {/* DONT TOUCH FROM HERE */}

            {/* <IconButton
                onClick={handleIconClick}
                aria-label="Account settings"
                aria-controls={open ? "account-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={open ? "true" : undefined}
                sx={{
                    width: 30,
                    height: 30,
                    p: 0,
                    border: `1px solid ${PureLightThemeCaaS.colors.alpha.gray[100]}`,
                    borderRadius: "20px",
                    backgroundColor: "black",
                    "&:hover": { backgroundColor: "black" },
                    zIndex: 1500,
                }}
            >
                <img src={accountIcon} alt="Account" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
            </IconButton>
            <Menu
                id="account-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleMenuClose}
                elevation={0}
                anchorOrigin={{ horizontal: "center", vertical: "bottom" }}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                sx={{ zIndex: 1500 }}  
                MenuListProps={{
                    sx: {
                        padding: "9px", paddingRight: "0px", paddingLeft: "0px",
                        fontSize: "14px",
                        // borderTopLeftRadius: 0,
                        // borderTopRightRadius: 0,
                    },
                }}
                slotProps={{
                    paper: {
                        ref: paperRef,
                        sx: {
                            minWidth: 40,
                            borderRadius: 0.5,
                            borderTopRightRadius: 0,
                            mt: 2,
                            p: 0,
                            bgcolor: "#fff",
                            border: "none",
                            boxShadow: "none",
                            overflow: "visible",
                            "&::before": {
                                mr: 1.5,
                                content: '""',
                                position: "absolute",
                                top: -8,
                                // right: 0,
                                left: "86.5%",
                                width: 0,
                                height: 0,
                                borderLeft: "10px solid transparent",
                                borderRight: "10px solid transparent",
                                borderBottom: "10px solid #fff",
                                transform: "rotate(135deg)",
                            },
                        },
                    }
                }}
            >
                <Box sx={{ p: 0, display: "flex", flexDirection: "column", gap: 0, }}>
                    <Button
                        variant="text"
                        size="small"
                        onClick={handleLogin}
                        disableElevation
                        sx={{
                            borderRadius: 0.5,
                            pl: 1,
                            pr: 1,
                            py: 0,
                            pb: 1,
                            justifyContent: "flex-start",
                            textAlign: "left",
                            height: "20px",
                            fontSize: "14px",
                            fontWeight: "normal",
                            bgcolor: "#fff",
                            color: "#000",
                            border: "none",
                            boxShadow: "none",
                            "&:hover": { bgcolor: "#fff", color: "#000", boxShadow: "none" },
                        }}
                    >
                        Login
                    </Button>
                    <Divider sx={{ width: "80%", mx: "auto", height: "2px" }} />
                    <Button
                        variant="text"
                        size="small"
                        onClick={handleLogin}
                        disableElevation
                        sx={{
                            borderRadius: 0.5,
                            pl: 1,
                            pr: 1,
                            py: 0,
                            pt: 1,
                            height: "20px",
                            fontSize: "14px",
                            fontWeight: "normal",
                            bgcolor: "#fff",
                            color: "#000",
                            border: "none",
                            boxShadow: "none",
                            "&:hover": { bgcolor: "#fff", color: "#000", boxShadow: "none" },
                        }}
                    >
                        Sign Up Free
                    </Button>
                </Box>
            </Menu>
            */}
        </>
    );
}
